'use client';

import { useEffect, useState, type ReactNode } from 'react';
import styles from './header.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

/** Scroll distance (px) past which the bar condenses — mirrors the spec's
 *  `--appbar-condense-at` (var(--s-16) = 64px). */
const CONDENSE_AT = 64;

/**
 * The glass app bar. Renders the `.cp-appbar` card and toggles its condensed
 * state on scroll (56 → 48px, deeper blur, shadow). Client so it can watch
 * scroll; the header content (brand/nav/actions) is passed in as children.
 */
export function HeaderShell({ children }: { children: ReactNode }) {
  const [condensed, setCondensed] = useState(false);

  useEffect(() => {
    const onScroll = () => setCondensed(window.scrollY > CONDENSE_AT);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /**
   * Publish the bar's CURRENT height as --appbar-h-live, so page content that
   * sticks to the bar's underside can follow it as it condenses.
   *
   * --appbar-h can't serve that purpose: it is the RESTING height and must stay
   * that way, because --appbar-reserve (the content top padding) is built from
   * it — recomputing that on scroll would reflow the page under the reader.
   * Without a live value, a sticky offset drifts 8px open the moment the bar
   * condenses, which is exactly what it looked like on /meal-plan.
   *
   * Driven off the `condensed` flag rather than a ResizeObserver: the height is
   * a plain class swap with no transition on it (see header.module.css), so the
   * flag IS the truth and flips in the same commit as the class. An observer
   * would add a frame of lag and a delivery dependency for no gain.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--appbar-h-live',
      condensed ? 'var(--s-12)' : 'var(--header-height)',
    );
    return () => {
      root.style.removeProperty('--appbar-h-live');
    };
  }, [condensed]);

  return (
    <header className={cx(styles.appbar, condensed && styles.condensed)}>
      <div className={styles.inner}>{children}</div>
    </header>
  );
}
